import type {
  AvisoAtraso,
  BlocoLocal,
  ModoVencimento,
  PapelAvisoAtraso,
  TipoBloco,
} from '../types/editor';

/** Nome que o core anuncia em `capabilities.features` quando sabe executar o modo novo. */
export const RECURSO_ESPERA_AVISAR_E_AGUARDAR = 'esperaAvisarEAguardar';

export const TIPOS_ESPERA_COM_VENCIMENTO: ReadonlySet<TipoBloco> = new Set<TipoBloco>([
  'AGUARDAR_EVENTO',
  'AGUARDAR_VISUALIZACAO',
  'AGUARDAR_CITACAO',
]);

export const PAPEIS_AVISO_ATRASO: ReadonlyArray<{ value: PapelAvisoAtraso; label: string }> = [
  { value: 'SECRETARIA', label: 'Secretaria' },
  { value: 'ARBITRO', label: 'Árbitro' },
  { value: 'REQUERENTE', label: 'Requerente' },
  { value: 'REQUERIDO', label: 'Requerido' },
];

const PAPEIS_VALIDOS: ReadonlySet<string> = new Set(PAPEIS_AVISO_ATRASO.map((p) => p.value));

// Faixas espelhadas do core (REQ-12): aqui só antecipam o erro.
export const FAIXA_REPETICAO_AVISO = { min: 1, max: 30, padrao: 1 } as const;
// Prazo da espera nos DOIS modos: o core executa qualquer modo com `MAX_TIMEOUT_DIAS_UTEIS`
// (`modoVencimentoDaEspera.ts`, aplicado em `arbitralWaitTimeout.ts`). Um teto menor só no
// "Seguir pelo ramo" barraria aqui fluxos que o motor aceita e executa.
export const FAIXA_TIMEOUT_ESPERA = { min: 1, max: 365 } as const;

type BlocoComParametros = Pick<BlocoLocal, 'tipo' | 'parametros'>;

export function modoVencimentoDe(bloco: BlocoComParametros): ModoVencimento {
  return TIPOS_ESPERA_COM_VENCIMENTO.has(bloco.tipo) &&
    bloco.parametros.modoVencimento === 'AVISAR_E_AGUARDAR'
    ? 'AVISAR_E_AGUARDAR'
    : 'SEGUIR_RAMO';
}

export function avisaEAguarda(bloco: BlocoComParametros): boolean {
  return modoVencimentoDe(bloco) === 'AVISAR_E_AGUARDAR';
}

/** Trocar para AVISAR_E_AGUARDAR com ações no ramo de timeout as deixaria órfãs (REQ-15). */
export function temBlocosNoRamoDeTimeout(bloco: BlocoComParametros): boolean {
  const ramo = bloco.parametros.aoTimeout;
  return Array.isArray(ramo) && ramo.length > 0;
}

function avisoAtrasoPadrao(): AvisoAtraso {
  return {
    para: ['SECRETARIA'],
    modeloNotificacaoId: null,
    repetirACadaDiasUteis: FAIXA_REPETICAO_AVISO.padrao,
  };
}

// Fora de qualquer faixa: a validação reprova, como o campo esvaziado no editor.
const REPETICAO_INVALIDA = 0;

/**
 * Lê `avisoAtraso` de parâmetros que podem vir de JSON importado, de `node.data` ou de uma
 * versão anterior do editor. O padrão só preenche chave AUSENTE (o seed do modo novo); chave
 * presente com formato inválido vira um valor que a validação reprova, em vez de virar o padrão
 * calado — `para: "ARBITRO"` não pode publicar avisando a Secretaria. Papéis desconhecidos e
 * repetidos são descartados, e o template nunca é inventado.
 */
export function lerAvisoAtraso(valor: unknown): AvisoAtraso {
  const padrao = avisoAtrasoPadrao();
  if (valor === undefined) return padrao;
  if (!valor || typeof valor !== 'object' || Array.isArray(valor)) {
    return { para: [], modeloNotificacaoId: null, repetirACadaDiasUteis: REPETICAO_INVALIDA };
  }
  const raw = valor as Record<string, unknown>;
  return {
    para: lerPapeis(raw.para, padrao.para),
    modeloNotificacaoId:
      typeof raw.modeloNotificacaoId === 'string' && raw.modeloNotificacaoId.length > 0
        ? raw.modeloNotificacaoId
        : null,
    repetirACadaDiasUteis: lerRepeticao(raw.repetirACadaDiasUteis, padrao.repetirACadaDiasUteis),
  };
}

function lerPapeis(valor: unknown, padrao: PapelAvisoAtraso[]): PapelAvisoAtraso[] {
  if (valor === undefined) return padrao;
  if (!Array.isArray(valor)) return [];
  return [...new Set(valor.filter((p): p is PapelAvisoAtraso => PAPEIS_VALIDOS.has(p as string)))];
}

function lerRepeticao(valor: unknown, padrao: number): number {
  if (valor === undefined) return padrao;
  return typeof valor === 'number' ? valor : REPETICAO_INVALIDA;
}

export function rotuloPapeisAviso(para: readonly PapelAvisoAtraso[]): string {
  const labels = PAPEIS_AVISO_ATRASO.filter((p) => para.includes(p.value)).map((p) =>
    p.label.toLowerCase(),
  );
  if (labels.length <= 1) return labels[0] ?? 'ninguém';
  return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
}
